import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power, Copy, Pencil } from 'lucide-react';
import { approvalMatrixApi, ApprovalMatrixRule } from '../../../api/admin/approval-matrix';
import { rolesApi } from '../../../api/admin/roles';
import { ApiError } from '../../../api/client';
import { SectionHeader } from '../../../components/SectionHeader';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { IconActionButton } from '../../../components/IconActionButton';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { MODULE_LABELS } from '../../../config/module-labels';

const CONDITION_TYPES = ['amount_lt', 'amount_gte', 'amount_between', 'project', 'sector', 'donor', 'department'] as const;
const CONDITION_LABELS: Record<string, string> = {
  amount_lt: 'Amount less than', amount_gte: 'Amount at or above', amount_between: 'Amount between (min-max)',
  project: 'Specific project (ID)', sector: 'Specific sector (ID)', donor: 'Specific donor (ID)', department: 'Specific department (ID)',
};

const formSchema = z.object({
  module: z.string().min(2, 'Choose what this rule is for.'),
  conditionType: z.enum(CONDITION_TYPES),
  conditionValue: z.string().min(1, 'Condition value is required.'),
  approverRole: z.string().min(1, 'Choose which role approves this.'),
  sequence: z.coerce.number().int().min(1).max(255),
  effectiveDate: z.string().optional(),
  priority: z.coerce.number().int().min(0).max(255).default(0),
});
type FormValues = z.infer<typeof formSchema>;

const previewSchema = z.object({
  module: z.string().min(1, 'Select a module to preview.'),
  amount: z.string().optional(),
  projectId: z.string().optional(),
  sectorId: z.string().optional(),
  donorId: z.string().optional(),
  departmentId: z.string().optional(),
});
type PreviewValues = z.infer<typeof previewSchema>;

/**
 * Extracted so AccessManagementPage can embed this as a tab without
 * duplicating logic - ApprovalMatrixPage below is now a thin wrapper
 * for direct/bookmarked links to the old route.
 *
 * Module and Approver Role are now real dropdowns (plain-language
 * labels, and real role names fetched live) instead of free-text
 * fields that required knowing exact internal strings by heart - the
 * single biggest usability gap this page had. Still reuses the SAME
 * approval_matrices table WorkflowEngine.buildApprovalSequence() has
 * always read - no parallel config store, no second engine.
 */
export function ApprovalMatrixContent() {
  const canManage = usePermission('admin.approval_matrix.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [moduleFilter, setModuleFilter] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ApprovalMatrixRule | null>(null);

  const rulesQuery = useQuery({ queryKey: ['approval-matrices', moduleFilter], queryFn: () => approvalMatrixApi.list(moduleFilter || undefined) });
  const modulesQuery = useQuery({ queryKey: ['approval-matrix-modules'], queryFn: () => approvalMatrixApi.modules() });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['approval-matrices'] }); queryClient.invalidateQueries({ queryKey: ['approval-matrix-modules'] }); };

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => editing ? approvalMatrixApi.update(editing.id, { ...values, effectiveDate: values.effectiveDate || null }) : approvalMatrixApi.create({ ...values, effectiveDate: values.effectiveDate || null }),
    onSuccess: () => { showSuccess(editing ? 'Rule updated.' : 'Rule created.'); invalidate(); setShowForm(false); setEditing(null); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save rule.'),
  });

  /**
   * Real warning, not a hard block: an EXACT duplicate (same module,
   * condition, sequence, AND approver role) as another active rule is
   * genuinely redundant and worth a second look before saving.
   * Deliberately does NOT warn when only the approver role differs at
   * the same module/condition/sequence - that represents a legitimate
   * "more than one person must approve at this step" pattern, not a
   * mistake, since createApproval() creates one pending approval per
   * matching rule and isFullyApproved() requires all of them.
   */
  const handleSaveSubmit = (values: FormValues) => {
    const exactDuplicate = (rulesQuery.data?.data ?? []).find((r) =>
      r.id !== editing?.id && r.is_active &&
      r.module === values.module && r.condition_type === values.conditionType && r.condition_value === values.conditionValue &&
      r.sequence === values.sequence && r.approver_role === values.approverRole
    );
    if (exactDuplicate) {
      const proceed = window.confirm(
        `An identical active rule already exists for "${MODULE_LABELS[values.module] ?? values.module}" (same condition, step, and approver role). Saving this will create a redundant duplicate. Continue anyway?`
      );
      if (!proceed) return;
    }
    saveMutation.mutate(values);
  };

  const toggleActiveMutation = useMutation({
    mutationFn: (rule: ApprovalMatrixRule) => rule.is_active ? approvalMatrixApi.deactivate(rule.id) : approvalMatrixApi.activate(rule.id),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update rule status.'),
  });

  /**
   * Real safety check: deactivating the only active rule left for a
   * module silently blocks every future submission in that area with
   * "No approval matrix is configured" - no confirmation, no warning,
   * exactly the same class of incident as the earlier
   * programme_director-has-no-limit issue, just one layer up. Rows
   * visible here are guaranteed to already be scoped to this rule's
   * module (either "All modules" or filtered to it), so this count is
   * accurate without an extra request.
   */
  const handleToggleActive = (rule: ApprovalMatrixRule) => {
    if (rule.is_active) {
      const otherActiveCount = (rulesQuery.data?.data ?? []).filter((r) => r.module === rule.module && r.is_active && r.id !== rule.id).length;
      if (otherActiveCount === 0) {
        const proceed = window.confirm(
          `This is the only active rule for "${MODULE_LABELS[rule.module] ?? rule.module}". Deactivating it will block every future submission in this area until a new rule is added. Continue?`
        );
        if (!proceed) return;
      }
    }
    toggleActiveMutation.mutate(rule);
  };

  const duplicateMutation = useMutation({
    mutationFn: (id: number) => approvalMatrixApi.duplicate(id),
    onSuccess: () => { showSuccess('Duplicated as a new inactive rule - activate it once you have finished editing.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to duplicate rule.'),
  });

  const openEdit = (rule: ApprovalMatrixRule) => {
    setEditing(rule);
    reset({
      module: rule.module, conditionType: rule.condition_type, conditionValue: rule.condition_value,
      approverRole: rule.approver_role, sequence: rule.sequence, effectiveDate: rule.effective_date ?? '', priority: rule.priority,
    });
    setShowForm(true);
  };

  const openCreate = () => { setEditing(null); reset({ module: moduleFilter, conditionType: 'amount_gte', conditionValue: '', approverRole: '', sequence: 1, effectiveDate: '', priority: 0 }); setShowForm(true); };

  const [previewResult, setPreviewResult] = useState<Awaited<ReturnType<typeof approvalMatrixApi.preview>>['data'] | null>(null);
  const { register: registerPreview, handleSubmit: handlePreviewSubmit } = useForm<PreviewValues>({ resolver: zodResolver(previewSchema) });
  const previewMutation = useMutation({
    mutationFn: (values: PreviewValues) => approvalMatrixApi.preview({
      module: values.module,
      amount: values.amount ? Number(values.amount) : undefined,
      projectId: values.projectId ? Number(values.projectId) : undefined,
      sectorId: values.sectorId ? Number(values.sectorId) : undefined,
      donorId: values.donorId ? Number(values.donorId) : undefined,
      departmentId: values.departmentId ? Number(values.departmentId) : undefined,
    }),
    onSuccess: (result) => setPreviewResult(result.data),
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to resolve preview.'),
  });

  return (
    <>
      <p className="muted">This decides which role approves what, at every step, for every part of VConnect that needs approval. To make a specific PERSON able to approve, go to the "People" tab and give them that role plus a money limit - this page only sets the general rule.</p>
      {canManage && <div className="form-actions"><button className="btn-primary" onClick={openCreate}>New Rule</button></div>}

      <div className="panel">
        <SectionHeader
          title="Rules"
          actions={
            <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
              <option value="">All modules</option>
              {modulesQuery.data?.data.map((m) => <option key={m} value={m}>{MODULE_LABELS[m] ?? m}</option>)}
            </select>
          }
        />
        <div className="panel-body">
          {rulesQuery.data && (
            <DataTable<ApprovalMatrixRule>
              rows={rulesQuery.data.data}
              rowKey={(r) => r.id}
              emptyMessage="No approval rules set yet for this module - submissions requiring approval will be blocked until at least one rule exists."
              columns={[
                { header: 'What this covers', render: (r) => MODULE_LABELS[r.module] ?? r.module },
                { header: 'When it applies', render: (r) => `${CONDITION_LABELS[r.condition_type] ?? r.condition_type}: ${r.condition_value}` },
                { header: 'Who approves', render: (r) => r.approver_role },
                { header: 'Step', render: (r) => r.sequence },
                { header: 'Priority', render: (r) => r.priority },
                { header: 'Starts', render: (r) => r.effective_date ?? 'Immediately' },
                { header: 'Status', render: (r) => r.is_active ? 'Active' : 'Inactive' },
                {
                  header: '',
                  render: (r) => canManage && (
                    <span className="icon-action-group">
                      <IconActionButton icon={Pencil} label="Edit" onClick={() => openEdit(r)} />
                      <IconActionButton icon={Copy} label="Duplicate" onClick={() => duplicateMutation.mutate(r.id)} />
                      <IconActionButton icon={Power} label={r.is_active ? 'Deactivate' : 'Activate'} danger={!!r.is_active} onClick={() => handleToggleActive(r)} />
                    </span>
                  ),
                },
              ]}
            />
          )}
        </div>
      </div>

      <div className="panel">
        <SectionHeader title="Test It" />
        <div className="panel-body">
          <p className="muted">Try some numbers and see exactly who would need to approve - the real logic, not a guess.</p>
          <form className="inline-form" onSubmit={handlePreviewSubmit((v) => previewMutation.mutate(v))}>
            <label>
              What are they approving?
              <select {...registerPreview('module')}>
                <option value="">- select -</option>
                {modulesQuery.data?.data.map((m) => <option key={m} value={m}>{MODULE_LABELS[m] ?? m}</option>)}
              </select>
            </label>
            <label>Amount<input type="number" step="0.01" {...registerPreview('amount')} /></label>
            <label>Project ID<input type="number" {...registerPreview('projectId')} /></label>
            <label>Sector ID<input type="number" {...registerPreview('sectorId')} /></label>
            <label>Donor ID<input type="number" {...registerPreview('donorId')} /></label>
            <label>Department ID<input type="number" {...registerPreview('departmentId')} /></label>
            <button type="submit" disabled={previewMutation.isPending}>{previewMutation.isPending ? 'Checking…' : 'Check Who Approves'}</button>
          </form>

          {previewResult && (
            previewResult.length === 0 ? (
              <p className="muted">No rule matches these numbers yet - this module has no approval rule set for this scenario.</p>
            ) : (
              <ol>
                {previewResult.map((step, i) => (
                  <li key={i}>
                    <strong>{step.approverRole}</strong> (step {step.sequence}) - because: {CONDITION_LABELS[step.matchedRule.conditionType] ?? step.matchedRule.conditionType} {step.matchedRule.conditionValue}
                  </li>
                ))}
              </ol>
            )
          )}
        </div>
      </div>

      {showForm && (
        <Modal title={editing ? 'Edit Approval Rule' : 'New Approval Rule'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <form onSubmit={handleSubmit(handleSaveSubmit)}>
            <label>
              What is this rule for?
              <select {...register('module')}>
                <option value="">- choose one -</option>
                {Object.entries(MODULE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              {errors.module && <span className="field-error">{errors.module.message}</span>}
            </label>
            <label>
              When does this rule apply?
              <select {...register('conditionType')}>
                {CONDITION_TYPES.map((c) => <option key={c} value={c}>{CONDITION_LABELS[c]}</option>)}
              </select>
            </label>
            <label>Condition Value<input placeholder="e.g. 500000, or 50000-200000 for 'between'" {...register('conditionValue')} />{errors.conditionValue && <span className="field-error">{errors.conditionValue.message}</span>}</label>
            <label>
              Who approves at this step?
              <select {...register('approverRole')}>
                <option value="">- choose a role -</option>
                {rolesQuery.data?.data.filter((r) => r.is_active).map((r) => <option key={r.id} value={r.name}>{r.name} - {r.description ?? 'no description'}</option>)}
              </select>
              {errors.approverRole && <span className="field-error">{errors.approverRole.message}</span>}
            </label>
            <label>Step Number (1 = first)<input type="number" min={1} {...register('sequence')} /></label>
            <label>Priority (higher resolves first when rules overlap)<input type="number" min={0} {...register('priority')} /></label>
            <label>Starts On (optional - blank means immediately)<input type="date" {...register('effectiveDate')} /></label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save'}</button>
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

export function ApprovalMatrixPage() {
  return (
    <div className="page">
      <PageHeader title="Approval Matrix" description="Configures which role approves what, at every step, for every part of VConnect that uses approval workflows." />
      <ApprovalMatrixContent />
    </div>
  );
}
